import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Column, Columns, EmptyState } from '@dixa/lib-shared-habla';
import { useCallback, useState } from 'react';
import { analytics } from 'app/utils/segment';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useSearchSplitLayout } from '../../../utils/useSearchSplitLayout';
import { SearchResultsEmpty } from '../../empty/searchResultsEmpty';
import { ContactTypeDropdown } from './components/dropdown/contactTypeDropdown';
import { ContactPreview } from './components/preview/contactPreview';
import { ContactResult } from './components/result/contactResult';
/**
 * Represents a component that is used to render the contacts tab of the search
 * results
 */
export const ContactsResults = ({ result, }) => {
    var _a, _b, _c, _d;
    const navigate = useAppNavigate();
    const resultsCount = {
        user: (_b = (_a = result === null || result === void 0 ? void 0 : result.user) === null || _a === void 0 ? void 0 : _a.nb_hits) !== null && _b !== void 0 ? _b : 0,
        company: (_d = (_c = result === null || result === void 0 ? void 0 : result.company) === null || _c === void 0 ? void 0 : _c.nb_hits) !== null && _d !== void 0 ? _d : 0,
    };
    const [contactType, setContactType] = useState((resultsCount.user && 'user') ||
        (resultsCount.company > 0 ? 'company' : 'user'));
    const [previewedHit, setPreviewedHit] = useState(null);
    const { leftColumnSize, rightColumnSize } = useSearchSplitLayout({
        isPreviewing: !!previewedHit,
    });
    const handleNavigateToContact = useCallback(() => {
        analytics.searchContactPreviewGoToClicked({ type: contactType });
        navigate({
            path: contactType === 'user' ? '/contacts/user/:id' : '/contacts/company/:id',
            params: {
                path: {
                    id: previewedHit === null || previewedHit === void 0 ? void 0 : previewedHit.id,
                },
            },
        });
    }, [contactType, navigate, previewedHit]);
    const handleSelectHit = useCallback((hit) => {
        if (hit.id === (previewedHit === null || previewedHit === void 0 ? void 0 : previewedHit.id))
            return;
        setPreviewedHit(hit);
        analytics.searchContactPreviewOpened({ type: contactType });
    }, [contactType, previewedHit, setPreviewedHit]);
    const handlePreviewClose = useCallback(() => {
        setPreviewedHit(null);
        analytics.searchContactPreviewClosed({ type: contactType });
    }, [contactType, setPreviewedHit]);
    const handleChangeContactType = useCallback((type) => {
        setContactType(type);
        setPreviewedHit(null);
        analytics.searchContactTypeChanged({ type });
    }, [setPreviewedHit]);
    const noResults = resultsCount[contactType] === 0;
    if (noResults) {
        return _jsx(SearchResultsEmpty, {});
    }
    return (_jsxs(Columns, { children: [_jsxs(Column, Object.assign({ size: leftColumnSize }, { children: [_jsx(Column.Row, Object.assign({ slottable: true, borderBottom: true }, { children: _jsx(Column.Slot, Object.assign({ placement: "start" }, { children: _jsx(ContactTypeDropdown, { counts: resultsCount, selected: contactType, onChange: handleChangeContactType }) })) })), _jsx(ContactResult, { result: contactType === 'user' ? result === null || result === void 0 ? void 0 : result.user : result === null || result === void 0 ? void 0 : result.company, selectedId: previewedHit === null || previewedHit === void 0 ? void 0 : previewedHit.id, onSelectHit: handleSelectHit })] })), _jsx(Column, Object.assign({ size: rightColumnSize }, { children: _jsx(Column.Row, Object.assign({ scrollable: true, noVerticalPadding: true, noHorizontalPadding: true }, { children: previewedHit ? (_jsx(ContactPreview, { hit: previewedHit, onGoToContact: handleNavigateToContact, onPreviewClose: handlePreviewClose })) : (_jsx(Box, Object.assign({ padding: "500 0" }, { children: _jsx(EmptyState, { title: "Select a contact", description: _jsxs("span", { children: ["Want to see a preview of the contact you're looking for? ", _jsx("br", {}), " Go ahead and click a contact to see it here."] }) }) }))) })) }))] }));
};
